import { PlayCircle, Compass, ExternalLink } from "lucide-react";

/** Renders an inline video (YouTube / Vimeo / MP4) and a virtual-tour iframe (Matterport / KulturArv360 / any embed) on PropertyDetail.
 *  Falls back gracefully when only one field is present. */

function toYouTubeEmbed(url) {
    if (!url) return null;
    try {
        const u = new URL(url);
        if (u.hostname.includes("youtu.be")) {
            return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
        }
        if (u.hostname.includes("youtube.com")) {
            const v = u.searchParams.get("v");
            if (v) return `https://www.youtube.com/embed/${v}`;
            // /embed/ links pass through
            if (u.pathname.startsWith("/embed/")) return url;
        }
        if (u.hostname.includes("vimeo.com")) {
            return `https://player.vimeo.com/video/${u.pathname.split("/").filter(Boolean)[0]}`;
        }
        return null;
    } catch { return null; }
}

export default function PropertyMedia({ videoUrl, virtualTourUrl }) {
    if (!videoUrl && !virtualTourUrl) return null;

    const youtubeEmbed = toYouTubeEmbed(videoUrl);

    return (
        <div className="space-y-5" data-testid="property-media">
            {videoUrl && (
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <PlayCircle className="w-4 h-4 text-gold-600" />
                        <h3 className="font-heading font-bold text-navy-500 text-lg">Property walkthrough</h3>
                    </div>
                    <div className="rounded-2xl overflow-hidden bg-navy-500/5 aspect-video border border-navy-500/10" data-testid="property-video">
                        {youtubeEmbed ? (
                            <iframe
                                src={youtubeEmbed}
                                title="Property video"
                                className="w-full h-full"
                                frameBorder="0"
                                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        ) : (
                            <video controls className="w-full h-full">
                                <source src={videoUrl} />
                            </video>
                        )}
                    </div>
                </div>
            )}

            {virtualTourUrl && (
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <Compass className="w-4 h-4 text-gold-600" />
                            <h3 className="font-heading font-bold text-navy-500 text-lg">360° Virtual Tour</h3>
                        </div>
                        <a
                            href={virtualTourUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="virtual-tour-fullscreen"
                            className="text-xs font-bold text-gold-700 hover:text-gold-800 inline-flex items-center gap-1"
                        >
                            Open fullscreen <ExternalLink className="w-3 h-3" />
                        </a>
                    </div>
                    <div className="rounded-2xl overflow-hidden bg-navy-500/5 aspect-video border border-navy-500/10" data-testid="property-virtual-tour">
                        <iframe
                            src={virtualTourUrl}
                            title="Virtual tour"
                            className="w-full h-full"
                            frameBorder="0"
                            allowFullScreen
                            allow="xr-spatial-tracking; vr; accelerometer; gyroscope"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
